import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  ShipperAssignmentsOverviewParams,
  ShipperAssignmentsResponse,
} from 'src/apis/assignment-service/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { handleErrorWithNotification } from 'src/utils/handle-error';

type Props = {
  url: string;
  params: ShipperAssignmentsOverviewParams;
};

export function useShipperAssignments({ url, params }: Props) {
  const shipperAssignments = useAxios(
    (axios, baseConfig, { url, params }: Props) =>
      axios.request<ShipperAssignmentsResponse>({
        ...baseConfig,
        url,
        params,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    const templatedUrl = urlTemplate.parse(url).expand({});

    shipperAssignments.execute({
      url: templatedUrl,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [url, params]);

  return shipperAssignments;
}

export type ShipperAssignmentsRequest = ReturnType<
  typeof useShipperAssignments
>;

export function useOptionalShipperAssignments({
  url,
  params,
}: {
  url?: string;
  params: ShipperAssignmentsOverviewParams;
}) {
  const shipperAssignments = useAxios(
    (axios, baseConfig, { url, params }: Props) =>
      axios.request<ShipperAssignmentsResponse>({
        ...baseConfig,
        url,
        params,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (!isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );

  useEffect(() => {
    if (!url) return;
    const templatedUrl = urlTemplate.parse(url).expand({});
    shipperAssignments.execute({
      url: templatedUrl,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [url, params]);

  if (!url) return;

  return shipperAssignments;
}

export type OptionalShipperAssignmentsRequest = ReturnType<
  typeof useOptionalShipperAssignments
>;
